/** SUNTRONIX ONE — Admin: Tenant Catalogue governance (Phase 1).
 *  Per-tenant Catalogue Settings + Tenant Price Lists + Preview-as-Tenant.
 *  Suntronix-only. Everything server-enforced; this is the control surface. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Store, Settings2, ListChecks, Eye, Plus, Trash2, Save, Loader2, Search,
  Tag, ShieldCheck, Package, Truck, RotateCcw, Repeat, LifeBuoy, Send,
} from "lucide-react";

const REQ_TONE = {
  REQUESTED: "bg-amber-50 text-amber-700", APPROVED: "bg-sky-50 text-sky-700",
  SCHEDULED: "bg-indigo-50 text-indigo-700", IN_PROGRESS: "bg-blue-50 text-blue-700",
  COMPLETED: "bg-emerald-50 text-emerald-700", REJECTED: "bg-rose-50 text-rose-700",
  CANCELLED: "bg-slate-100 text-slate-500",
};

const STATUS_TINT = {
  PILOT: "bg-amber-50 text-amber-700", ACTIVE: "bg-emerald-50 text-emerald-700",
  SUSPENDED: "bg-rose-50 text-rose-700",
};
const money = (n, cur = "INR") =>
  n == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(n).toLocaleString("en-IN")}`;

function Toggle({ label, hint, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-slate-50">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

// ---------------------------------------------------------------- Settings dialog
function SettingsDialog({ tenant, onClose, onSaved }) {
  const [s, setS] = useState(null);
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [priceLists, setPriceLists] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!tenant) return;
    api.get(`/tenant-catalogue/admin/settings/${tenant.tenant_id}`).then(({ data }) => setS(data));
    api.get(`/tenant-catalogue/admin/price-lists`, { params: { tenant_id: tenant.tenant_id } })
      .then(({ data }) => setPriceLists(data.items || []));
    api.get(`/tenant-catalogue/admin/preview/${tenant.tenant_id}`, { params: { limit: 1 } })
      .then(({ data }) => setFacets(data.facets || { brands: [], categories: [] }))
      .catch(() => {});
  }, [tenant]);

  const toggleIn = (key, value) => {
    const arr = s[key] || [];
    set(key, arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/tenant-catalogue/admin/settings/${tenant.tenant_id}`, {
        catalogue_enabled: s.catalogue_enabled, direct_order_enabled: s.direct_order_enabled,
        request_quote_enabled: s.request_quote_enabled, price_visibility: s.price_visibility,
        stock_visibility: s.stock_visibility, visibility_mode: s.visibility_mode,
        allowed_brands: s.allowed_brands, allowed_categories: s.allowed_categories,
        allowed_products: s.allowed_products, price_list_id: s.price_list_id || null,
        po_required: s.po_required, po_upload_required: s.po_upload_required,
        currency: s.currency, status: s.status,
        standard_products: s.standard_products || [],
        approval_enabled: s.approval_enabled, approval_threshold: Number(s.approval_threshold) || 0,
        approver_emails: s.approver_emails || [],
      });
      toast.success("Catalogue settings saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="tenant-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-sky-600" />{tenant?.name} — Catalogue Settings</DialogTitle>
        </DialogHeader>
        {!s ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="overflow-y-auto flex-1 min-h-0 space-y-4 pr-1">
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Access</div>
              <Toggle label="Catalogue enabled" hint="Tenant can see the catalogue portal" checked={s.catalogue_enabled} onChange={(v) => set("catalogue_enabled", v)} testid="set-catalogue-enabled" />
              <Toggle label="Direct ordering" hint="Allow Place Order without a quote" checked={s.direct_order_enabled} onChange={(v) => set("direct_order_enabled", v)} testid="set-direct-order" />
              <Toggle label="Request quote" hint="Allow Request Formal Quotation" checked={s.request_quote_enabled} onChange={(v) => set("request_quote_enabled", v)} testid="set-request-quote" />
              <div className="flex items-center justify-between py-2">
                <div className="text-[13px] font-medium text-slate-800">Status</div>
                <select value={s.status} onChange={(e) => set("status", e.target.value)} data-testid="set-status" className="h-8 rounded-md border border-slate-200 text-sm px-2">
                  {["PILOT", "ACTIVE", "SUSPENDED"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Pricing</div>
              <div className="flex items-center justify-between py-2 border-b border-slate-50">
                <div className="text-[13px] font-medium text-slate-800">Price visibility</div>
                <div className="flex gap-1">
                  {["SHOW", "HIDE"].map((x) => (
                    <button key={x} onClick={() => set("price_visibility", x)} data-testid={`set-pv-${x.toLowerCase()}`}
                      className={"px-3 py-1 rounded-md text-[12px] border " + (s.price_visibility === x ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200")}>
                      {x === "SHOW" ? "Show prices" : "Hide → Request Quote"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-slate-50">
                <div className="text-[13px] font-medium text-slate-800">Price list</div>
                <select value={s.price_list_id || ""} onChange={(e) => set("price_list_id", e.target.value)} data-testid="set-price-list" className="h-8 rounded-md border border-slate-200 text-sm px-2 max-w-[240px]">
                  <option value="">Standard selling price</option>
                  {priceLists.map((pl) => { const label = `${pl.name} (${pl.item_count} rules)`; return <option key={pl.id} value={pl.id}>{label}</option>; })}
                </select>
              </div>
              <div className="flex items-center justify-between py-2">
                <div className="text-[13px] font-medium text-slate-800">Currency</div>
                <select value={s.currency} onChange={(e) => set("currency", e.target.value)} data-testid="set-currency" className="h-8 rounded-md border border-slate-200 text-sm px-2">
                  {["INR", "USD", "EUR"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </div>
              <Toggle label="Show available stock" hint="Expose available quantity" checked={s.stock_visibility} onChange={(v) => set("stock_visibility", v)} testid="set-stock" />
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Product visibility</div>
              <div className="flex items-center justify-between py-2 border-b border-slate-50">
                <div className="text-[13px] font-medium text-slate-800">Scope</div>
                <select value={s.visibility_mode} onChange={(e) => set("visibility_mode", e.target.value)} data-testid="set-visibility-mode" className="h-8 rounded-md border border-slate-200 text-sm px-2">
                  <option value="ALL">All products</option>
                  <option value="APPROVED_BRANDS">Approved brands only</option>
                  <option value="APPROVED_CATEGORIES">Approved categories only</option>
                  <option value="APPROVED_PRODUCTS">Approved products only</option>
                </select>
              </div>
              {s.visibility_mode === "APPROVED_BRANDS" && (
                <div className="py-2" data-testid="brand-picker">
                  <div className="text-[11px] text-slate-400 mb-1.5">Allowed brands ({(s.allowed_brands || []).length})</div>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {facets.brands.map((b) => {
                      const on = (s.allowed_brands || []).includes(b);
                      return <button key={b} onClick={() => toggleIn("allowed_brands", b)}
                        className={"px-2.5 py-1 rounded-full text-[11px] border " + (on ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200")}>{b}</button>;
                    })}
                  </div>
                </div>
              )}
              {s.visibility_mode === "APPROVED_CATEGORIES" && (
                <div className="py-2" data-testid="category-picker">
                  <div className="text-[11px] text-slate-400 mb-1.5">Allowed categories ({(s.allowed_categories || []).length})</div>
                  <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                    {facets.categories.map((c) => {
                      const on = (s.allowed_categories || []).includes(c);
                      return <button key={c} onClick={() => toggleIn("allowed_categories", c)}
                        className={"px-2.5 py-1 rounded-full text-[11px] border " + (on ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200")}>{c}</button>;
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Purchase orders</div>
              <Toggle label="PO required" hint="Order needs a Customer PO number" checked={s.po_required} onChange={(v) => set("po_required", v)} testid="set-po-required" />
              <Toggle label="PO upload required" hint="Order needs a PO document uploaded" checked={s.po_upload_required} onChange={(v) => set("po_upload_required", v)} testid="set-po-upload" />
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Approval workflow</div>
              <Toggle label="Require internal approval" hint="Orders above the limit need a tenant approver" checked={s.approval_enabled} onChange={(v) => set("approval_enabled", v)} testid="set-approval-enabled" />
              {s.approval_enabled && (
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[13px] text-slate-700">Approval limit (₹)</span>
                    <Input type="number" value={s.approval_threshold ?? 0} onChange={(e) => set("approval_threshold", e.target.value)} data-testid="set-approval-threshold" className="h-8 w-32 text-sm" />
                  </div>
                  <div>
                    <div className="text-[11px] text-slate-400 mb-1">Approver emails (comma separated)</div>
                    <Input value={(s.approver_emails || []).join(", ")} onChange={(e) => set("approver_emails", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} data-testid="set-approver-emails" className="h-8 text-sm" placeholder="approver@tenant.com" />
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Tenant standard products</div>
              <p className="text-[11px] text-slate-400 mb-1.5">Product codes surfaced first on the tenant's Home & Catalogue (comma or newline separated).</p>
              <Textarea rows={2} value={(s.standard_products || []).join(", ")} onChange={(e) => set("standard_products", e.target.value.split(/[\n,]/).map((x) => x.trim()).filter(Boolean))} data-testid="set-standard-products" className="text-sm font-mono" placeholder="PRO2668, MAC14-001" />
              <div className="text-[11px] text-slate-400 mt-1">{(s.standard_products || []).length} standard product(s)</div>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !s} data-testid="settings-save" className="bg-sky-600 hover:bg-sky-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save settings
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Preview dialog
function PreviewDialog({ tenant, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => {
    if (!tenant) return;
    api.get(`/tenant-catalogue/admin/preview/${tenant.tenant_id}`, { params: { q: q || undefined, limit: 12 } })
      .then(({ data }) => setData(data));
  }, [tenant, q]);
  return (
    <Dialog open={!!tenant} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="tenant-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-sky-600" />Preview as {tenant?.name}</DialogTitle>
        </DialogHeader>
        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the tenant catalogue…" className="pl-9 h-9" data-testid="preview-search" />
        </div>
        {!data ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="overflow-y-auto flex-1 min-h-0">
            <div className="text-[12px] text-slate-500 mb-2">{data.total} products visible · prices {data.price_visibility}</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {data.items.map((p) => (
                <div key={p.product_code} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.brand} · {p.category}</div>
                  <div className="text-sm font-medium text-slate-800 truncate">{p.product_name}</div>
                  <div className="text-[11px] font-mono text-slate-400">{p.oem_part_number || p.sku || p.product_code}</div>
                  <div className="mt-1.5">
                    {p.pricing?.mode === "PRICE"
                      ? <span className="text-sm font-semibold text-slate-900">{money(p.pricing.unit_price, p.pricing.currency)}</span>
                      : <Badge className="bg-amber-50 text-amber-700">Request Quote</Badge>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Price list editor
const EMPTY_ITEM = { scope: "BRAND", match: "", rule: "DISCOUNT_PCT", value: 0, min_qty: 1 };
function PriceListEditor({ list, onClose, onSaved }) {
  const [form, setForm] = useState(list?.id ? null : { name: "", code: "", currency: "INR", status: "ACTIVE", items: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (list?.id) api.get(`/tenant-catalogue/admin/price-lists/${list.id}`).then(({ data }) => setForm(data));
  }, [list]);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setItem = (i, k, v) => setForm((p) => ({ ...p, items: p.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setForm((p) => ({ ...p, items: [...(p.items || []), { ...EMPTY_ITEM }] }));
  const rmItem = (i) => setForm((p) => ({ ...p, items: p.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, code: form.code, currency: form.currency, status: form.status,
        items: (form.items || []).map((it) => ({ ...it, value: Number(it.value) || 0, min_qty: Number(it.min_qty) || 1 })) };
      if (list?.id) await api.put(`/tenant-catalogue/admin/price-lists/${list.id}`, payload);
      else await api.post(`/tenant-catalogue/admin/price-lists`, payload);
      toast.success("Price list saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="price-list-editor">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Tag className="h-5 w-5 text-sky-600" />{list?.id ? "Edit" : "New"} price list</DialogTitle></DialogHeader>
        {!form ? <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div> : (
          <div className="overflow-y-auto flex-1 min-h-0 space-y-3 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-[12px] text-slate-600">Name</label><Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="pl-name" className="h-9 mt-1" /></div>
              <div><label className="text-[12px] text-slate-600">Code</label><Input value={form.code} onChange={(e) => setF("code", e.target.value)} data-testid="pl-code" className="h-9 mt-1" /></div>
            </div>
            <div className="flex items-center justify-between">
              <div className="text-[13px] font-semibold text-slate-800">Rules</div>
              <Button variant="create" size="sm" onClick={addItem} data-testid="pl-add-rule"><Plus className="h-3.5 w-3.5 mr-1" />Add rule</Button>
            </div>
            {(form.items || []).length === 0 && <div className="text-[12px] text-slate-400 text-center py-4">No rules — products fall back to standard selling price.</div>}
            {(form.items || []).map((it, i) => (
              <div key={i} data-testid={`pl-rule-${i}`} className="flex items-center gap-2 rounded-md border border-slate-200 p-2">
                <select value={it.scope} onChange={(e) => setItem(i, "scope", e.target.value)} className="h-8 rounded-md border border-slate-200 text-[12px] px-1">
                  {["PRODUCT", "BRAND", "CATEGORY"].map((x) => <option key={x}>{x}</option>)}
                </select>
                <Input value={it.match} onChange={(e) => setItem(i, "match", e.target.value)} placeholder="match value" className="h-8 text-[12px] flex-1" data-testid={`pl-match-${i}`} />
                <select value={it.rule} onChange={(e) => setItem(i, "rule", e.target.value)} className="h-8 rounded-md border border-slate-200 text-[12px] px-1">
                  <option value="DISCOUNT_PCT">Discount %</option>
                  <option value="MARKUP_PCT">Markup %</option>
                  <option value="FIXED">Fixed</option>
                  <option value="SPECIAL">Special</option>
                </select>
                <Input type="number" value={it.value} onChange={(e) => setItem(i, "value", e.target.value)} className="h-8 text-[12px] w-20" data-testid={`pl-value-${i}`} />
                <Input type="number" value={it.min_qty} onChange={(e) => setItem(i, "min_qty", e.target.value)} className="h-8 text-[12px] w-14" title="Min qty" />
                <button onClick={() => rmItem(i)} className="text-rose-500 p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form} data-testid="pl-save" className="bg-sky-600 hover:bg-sky-700 text-white">{saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- order fulfilment
function FulfilDialog({ order, onClose, onSaved }) {
  const FLOW = ["ORDER_RECEIVED", "PO_VERIFIED", "PROCESSING", "READY_TO_DISPATCH", "DISPATCHED", "DELIVERED", "COMPLETED"];
  const [status, setStatus] = useState(order.status);
  const [courier, setCourier] = useState("");
  const [awb, setAwb] = useState("");
  const [eta, setEta] = useState("");
  const [serials, setSerials] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { status };
      if (courier || awb || eta) { body.courier = courier; body.awb = awb; body.eta = eta; }
      if (status === "DELIVERED" && serials.trim()) {
        body.serials = serials.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
          const [product_code, serial, assigned_user] = l.split(",").map((x) => x?.trim());
          return { product_code, serial, assigned_user };
        });
      }
      await api.patch(`/tenant-catalogue/admin/orders/${order.id}/fulfil`, body);
      toast.success("Order updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="fulfil-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-sky-600" />Fulfil {order.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-[12px] text-slate-600">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="fulfil-status" className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
              {FLOW.map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-[11px] text-slate-600">Courier</label><Input value={courier} onChange={(e) => setCourier(e.target.value)} data-testid="fulfil-courier" className="h-8 text-sm mt-1" /></div>
            <div><label className="text-[11px] text-slate-600">AWB</label><Input value={awb} onChange={(e) => setAwb(e.target.value)} data-testid="fulfil-awb" className="h-8 text-sm mt-1" /></div>
            <div><label className="text-[11px] text-slate-600">ETA</label><Input type="date" value={eta} onChange={(e) => setEta(e.target.value)} className="h-8 text-sm mt-1" /></div>
          </div>
          {status === "DELIVERED" && (
            <div><label className="text-[12px] text-slate-600">Serials (product_code, serial, user — one per line)</label>
              <Textarea rows={2} value={serials} onChange={(e) => setSerials(e.target.value)} data-testid="fulfil-serials" className="text-sm font-mono mt-1" placeholder="PRO2668, SN-001, Ravi" /></div>
          )}
          <Button onClick={save} disabled={busy} data-testid="fulfil-save" className="w-full bg-sky-600 hover:bg-sky-700 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Update order</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Page
function LifecycleAdminDialog({ req, onClose, onSaved }) {
  const [status, setStatus] = useState(req.status);
  const [note, setNote] = useState("");
  const [cert, setCert] = useState(req.certificate_url || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/tenant-catalogue/admin/lifecycle/${req.id}`, { status, note, certificate_url: cert });
      toast.success("Request updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const isDisposal = req.request_type === "RECYCLE" || req.request_type === "RETIRE";
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col" data-testid="admin-lc-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Repeat className="h-5 w-5 text-sky-600" />{req.request_no}</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-y-auto text-[13px]">
          <div className="rounded-lg bg-slate-50 p-3 space-y-1">
            <div className="font-medium text-slate-800">{req.request_type} · {req.product_name}</div>
            <div className="text-[12px] text-slate-500 font-mono">SN {req.serial}</div>
            <div className="text-[12px] text-slate-600">From: {req.current_user || "—"} @ {req.current_location || "—"}</div>
            {req.request_type === "REDEPLOY" && <div className="text-[12px] text-slate-600">To: {req.target_user || "—"} @ {req.target_location || "—"}</div>}
            <div className="text-[12px] text-slate-600">Reason: {req.reason}</div>
            {req.pickup_required && <Badge className="bg-amber-50 text-amber-700">Reverse pickup requested</Badge>}
            {req.data_wipe_required && <Badge className="bg-rose-50 text-rose-700">Secure data wipe</Badge>}
          </div>
          <div><label className="text-[12px] text-slate-600">Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-lc-status" className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
              {["REQUESTED", "APPROVED", "SCHEDULED", "IN_PROGRESS", "COMPLETED", "REJECTED"].map((x) => <option key={x}>{x}</option>)}
            </select>
          </div>
          {isDisposal && (
            <div><label className="text-[12px] text-slate-600">Disposal / data-destruction certificate URL</label><Input value={cert} onChange={(e) => setCert(e.target.value)} data-testid="admin-lc-cert" className="h-9 mt-1" placeholder="https://…" /></div>
          )}
          <div><label className="text-[12px] text-slate-600">Note</label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-lc-note" className="text-sm mt-1" /></div>
          {req.timeline?.length > 0 && (
            <div className="border-l-2 border-slate-100 pl-3 space-y-1.5">
              {req.timeline.map((t, i) => <div key={i} className="text-[11px] text-slate-500"><span className="text-slate-700">{t.event}</span> · {t.at ? new Date(t.at).toLocaleString() : ""}{t.note ? ` — ${t.note}` : ""}</div>)}
            </div>
          )}
          <Button onClick={save} disabled={busy} data-testid="admin-lc-save" className="w-full bg-slate-900 hover:bg-slate-800 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Update</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SupportAdminDialog({ ticket, onClose, onSaved }) {
  const [t, setT] = useState(ticket);
  const [status, setStatus] = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority || "MEDIUM");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/tenant-catalogue/admin/support/${t.id}`, { status, priority, reply });
      toast.success("Ticket updated"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="admin-support-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 text-[15px]"><LifeBuoy className="h-5 w-5 text-sky-600" />{t.ticket_no}</DialogTitle></DialogHeader>
        <div className="space-y-3 overflow-y-auto flex-1">
          <div className="text-sm font-medium text-slate-800">{t.subject}</div>
          <div className="text-[11px] text-slate-400">{t.raised_by_name} · {t.tenant_id}{t.serial ? ` · SN ${t.serial}` : ""}</div>
          <div className="space-y-2">
            {(t.thread || []).length === 0 && <div className="text-[12px] text-slate-400">{t.description}</div>}
            {(t.thread || []).map((m, i) => (
              <div key={i} className={"rounded-lg px-3 py-2 text-[13px] max-w-[85%] " + (m.role === "agent" ? "bg-sky-50 ml-auto" : "bg-slate-100")}>
                <div className="text-[10px] text-slate-400 mb-0.5">{m.role === "agent" ? "You (Support)" : m.by_name} · {m.at ? new Date(m.at).toLocaleString() : ""}</div>
                {m.text}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[12px] text-slate-600">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-support-status" className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
                {["OPEN", "IN_PROGRESS", "AWAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div><label className="text-[12px] text-slate-600">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="admin-support-priority" className="w-full h-9 rounded-md border border-slate-200 text-sm px-2 mt-1">
                {["LOW", "MEDIUM", "HIGH", "URGENT"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-[12px] text-slate-600">Reply to customer</label><Textarea rows={2} value={reply} onChange={(e) => setReply(e.target.value)} data-testid="admin-support-reply" className="text-sm mt-1" /></div>
          <Button onClick={save} disabled={busy} data-testid="admin-support-save" className="w-full bg-slate-900 hover:bg-slate-800 text-white">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Update ticket</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function TenantCatalogueAdmin() {
  const [tab, setTab] = useState("tenants");
  const [tenants, setTenants] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [q, setQ] = useState("");
  const [settingsFor, setSettingsFor] = useState(null);
  const [previewFor, setPreviewFor] = useState(null);
  const [editList, setEditList] = useState(null);
  const [orders, setOrders] = useState([]);
  const [fulfilFor, setFulfilFor] = useState(null);
  const [returns, setReturns] = useState([]);
  const [lifecycle, setLifecycle] = useState([]);
  const [support, setSupport] = useState([]);
  const [lcFor, setLcFor] = useState(null);
  const [supportFor, setSupportFor] = useState(null);

  const load = useCallback(async () => {
    const [t, pl, o, r, lc, sup] = await Promise.all([
      api.get("/tenant-catalogue/admin/tenants"),
      api.get("/tenant-catalogue/admin/price-lists"),
      api.get("/tenant-catalogue/admin/orders"),
      api.get("/tenant-catalogue/admin/returns"),
      api.get("/tenant-catalogue/admin/lifecycle"),
      api.get("/tenant-catalogue/admin/support"),
    ]);
    setTenants(t.data.items || []);
    setPriceLists(pl.data.items || []);
    setOrders(o.data.items || []);
    setReturns(r.data.items || []);
    setLifecycle(lc.data.items || []);
    setSupport(sup.data.items || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const delList = async (id) => {
    if (!window.confirm("Delete this price list?")) return;
    await api.delete(`/tenant-catalogue/admin/price-lists/${id}`);
    toast.success("Price list deleted"); load();
  };
  const genInvoice = async (oid) => {
    try { const { data } = await api.post(`/tenant-catalogue/admin/orders/${oid}/invoice`); toast.success(`Invoice ${data.invoice_no}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setRma = async (rid, status) => {
    try { await api.patch(`/tenant-catalogue/admin/returns/${rid}`, { status }); toast.success("Return updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const shown = tenants.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()) || t.tenant_id.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="tenant-catalogue-admin">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Administration</div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2"><Store className="h-6 w-6 text-sky-600" />Tenant Catalogue</h1>
        <p className="text-sm text-slate-500">Control catalogue access, pricing and visibility per customer tenant.</p>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[{ id: "tenants", label: "Tenants", icon: Store }, { id: "pricelists", label: "Price Lists", icon: ListChecks }, { id: "orders", label: "Orders", icon: Package }, { id: "lifecycle", label: "Lifecycle", icon: Repeat }, { id: "support", label: "Support", icon: LifeBuoy }, { id: "returns", label: "Returns", icon: RotateCcw }].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tc-tab-${t.id}`}
            className={"flex items-center gap-1.5 px-3 py-2 text-[13px] border-b-2 -mb-px " + (tab === t.id ? "border-sky-600 text-sky-700 font-medium" : "border-transparent text-slate-500")}>
            <t.icon className="h-3.5 w-3.5" />{t.label}
          </button>
        ))}
      </div>

      {tab === "tenants" && (
        <div className="space-y-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tenants…" className="pl-9 h-9" data-testid="tenant-search" />
          </div>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {shown.map((t) => (
              <div key={t.tenant_id} data-testid={`tenant-row-${t.tenant_id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{t.name}</div>
                  <div className="text-[11px] text-slate-400 font-mono">{t.tenant_id}</div>
                </div>
                <Badge className={t.catalogue_enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>{t.catalogue_enabled ? "Enabled" : "Off"}</Badge>
                <Badge className="bg-slate-100 text-slate-600">{t.price_visibility === "SHOW" ? "Prices shown" : "RFQ"}</Badge>
                <Badge className={STATUS_TINT[t.status]}>{t.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => setPreviewFor(t)} data-testid={`tenant-preview-${t.tenant_id}`}><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                <Button size="sm" onClick={() => setSettingsFor(t)} data-testid={`tenant-configure-${t.tenant_id}`} className="bg-slate-900 hover:bg-slate-800 text-white"><Settings2 className="h-3.5 w-3.5 mr-1" />Configure</Button>
              </div>
            ))}
            {shown.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No tenants found.</div>}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-slate-400"><ShieldCheck className="h-3.5 w-3.5" />Tenant isolation is enforced server-side. Preview is read-only.</div>
        </div>
      )}

      {tab === "pricelists" && (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button variant="create" onClick={() => setEditList({})} data-testid="pl-new" className=""><Plus className="h-4 w-4 mr-1.5" />New price list</Button>
          </div>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {priceLists.map((pl) => (
              <div key={pl.id} data-testid={`pl-row-${pl.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                <Tag className="h-4 w-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{pl.name} <span className="text-[11px] text-slate-400">· {pl.code || "—"}</span></div>
                  <div className="text-[11px] text-slate-400">{pl.item_count} rule(s) · {pl.currency}</div>
                </div>
                <Badge className={pl.status === "ACTIVE" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}>{pl.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => setEditList(pl)} data-testid={`pl-edit-${pl.id}`}>Edit</Button>
                <button onClick={() => delList(pl.id)} className="text-rose-500 p-1" data-testid={`pl-del-${pl.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {priceLists.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No price lists yet. Create one to give tenants contract pricing.</div>}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="admin-orders">
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
              <Package className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{o.order_no} <span className="text-[11px] text-slate-400">· {o.tenant_id}</span></div>
                <div className="text-[11px] text-slate-400">{o.items?.length || 0} item(s) · ₹{Number(o.subtotal || 0).toLocaleString("en-IN")}{o.po_number ? ` · PO ${o.po_number}` : ""}</div>
              </div>
              <Badge className="bg-slate-100 text-slate-600">{o.status_label}</Badge>
              {(o.status === "DELIVERED" || o.status === "COMPLETED") && <Button size="sm" variant="outline" onClick={() => genInvoice(o.id)} data-testid={`admin-invoice-${o.id}`}>Invoice</Button>}
              <Button size="sm" onClick={() => setFulfilFor(o)} data-testid={`admin-fulfil-${o.id}`} disabled={o.status === "PENDING_APPROVAL"} className="bg-sky-600 hover:bg-sky-700 text-white"><Truck className="h-3.5 w-3.5 mr-1" />Fulfil</Button>
            </div>
          ))}
          {orders.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No tenant orders yet.</div>}
        </div>
      )}

      {tab === "lifecycle" && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="admin-lifecycle">
          {lifecycle.map((r) => (
            <div key={r.id} data-testid={`admin-lc-${r.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
              <Repeat className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{r.request_no} · {r.request_type} <span className="text-[11px] text-slate-400">· {r.tenant_id}</span></div>
                <div className="text-[11px] text-slate-400 truncate">{r.serial ? `SN ${r.serial} · ` : ""}{r.product_name}{r.target_user ? ` → ${r.target_user}` : ""}{r.target_location ? ` @ ${r.target_location}` : ""}</div>
              </div>
              <Badge className={REQ_TONE[r.status] || ""}>{r.status_label || r.status}</Badge>
              <Button size="sm" onClick={() => setLcFor(r)} data-testid={`admin-lc-manage-${r.id}`} className="bg-sky-600 hover:bg-sky-700 text-white">Manage</Button>
            </div>
          ))}
          {lifecycle.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No lifecycle requests yet.</div>}
        </div>
      )}

      {tab === "support" && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="admin-support-list">
          {support.map((s) => (
            <div key={s.id} data-testid={`admin-support-${s.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
              <LifeBuoy className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{s.ticket_no} · {s.subject} <span className="text-[11px] text-slate-400">· {s.tenant_id}</span></div>
                <div className="text-[11px] text-slate-400 truncate">{s.category || "—"} · {s.serial ? `SN ${s.serial} · ` : ""}{s.raised_by_name}</div>
              </div>
              <Badge className={s.priority === "URGENT" || s.priority === "HIGH" ? "bg-rose-50 text-rose-700" : "bg-slate-100 text-slate-600"}>{s.priority || "MEDIUM"}</Badge>
              <Badge className="bg-sky-50 text-sky-700">{s.status}</Badge>
              <Button size="sm" onClick={() => setSupportFor(s)} data-testid={`admin-support-manage-${s.id}`} className="bg-sky-600 hover:bg-sky-700 text-white">Open</Button>
            </div>
          ))}
          {support.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No support tickets.</div>}
        </div>
      )}

      {tab === "returns" && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100" data-testid="admin-returns">
          {returns.map((r) => (
            <div key={r.id} data-testid={`admin-rma-${r.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <RotateCcw className="h-4 w-4 text-slate-400" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{r.rma_no} · {r.rma_type} <span className="text-[11px] text-slate-400">· {r.tenant_id}</span></div>
                <div className="text-[11px] text-slate-400">{r.serial ? `SN ${r.serial} · ` : ""}{r.reason}</div>
              </div>
              <select value={r.status} onChange={(e) => setRma(r.id, e.target.value)} data-testid={`admin-rma-status-${r.id}`} className="h-8 rounded-md border border-slate-200 text-[12px] px-1">
                {["REQUESTED", "APPROVED", "REJECTED", "IN_TRANSIT", "RESOLVED"].map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
          ))}
          {returns.length === 0 && <div className="text-center py-10 text-slate-400 text-sm">No return requests.</div>}
        </div>
      )}

      {settingsFor && <SettingsDialog tenant={settingsFor} onClose={() => setSettingsFor(null)} onSaved={() => { setSettingsFor(null); load(); }} />}
      {previewFor && <PreviewDialog tenant={previewFor} onClose={() => setPreviewFor(null)} />}
      {editList && <PriceListEditor list={editList} onClose={() => setEditList(null)} onSaved={() => { setEditList(null); load(); }} />}
      {fulfilFor && <FulfilDialog order={fulfilFor} onClose={() => setFulfilFor(null)} onSaved={() => { setFulfilFor(null); load(); }} />}
      {lcFor && <LifecycleAdminDialog req={lcFor} onClose={() => setLcFor(null)} onSaved={() => { setLcFor(null); load(); }} />}
      {supportFor && <SupportAdminDialog ticket={supportFor} onClose={() => setSupportFor(null)} onSaved={() => { setSupportFor(null); load(); }} />}
    </div>
  );
}
